import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Route,
  Routes,
  TitleStrategy,
  withNavigationErrorHandler,
} from '@angular/router';
import { HttpErrorResponse, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { firstValueFrom, of, catchError, switchMap } from 'rxjs';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { orderCacheInterceptor } from './core/interceptors/order-cache.interceptor';
import { pageProgressInterceptor } from './core/interceptors/page-progress.interceptor';
import { GlobalErrorHandler } from './core/errors/global-error.handler';
import { ChunkReloadService } from './core/services/chunk-reload.service';
import { PageTitleStrategy } from './core/services/page-title.strategy';
import { AuthService } from './core/services/auth.service';
import { AppVersionService } from './core/services/app-version.service';
import { LanguageSyncService } from './core/services/language-sync.service';
import { SelectSheetService } from './shared/components/select-sheet/select-sheet.component';

/** A signed-in reload: the code of the page opened downloads while the profile loads, not
 *  after it (the router loads a page's code only once the session check let it through). */
export function preloadOpenedPage(
  auth: AuthService,
  path = location.pathname,
  list: Routes = routes
): void {
  if (!auth.isAuthenticated()) return;
  const loading = findPage(list, path.split('/').filter(Boolean))?.loadComponent?.();
  // A failed download is the router's to report, when it loads the page itself
  Promise.resolve(loading).catch(() => undefined);
}

/** The route of these path segments, matched by literal paths only (no :params). */
function findPage(list: Routes, segments: string[]): Route | undefined {
  for (const route of list) {
    const own = (route.path ?? '').split('/').filter(Boolean);
    if (own.length > segments.length || own.some((part, i) => part !== segments[i])) continue;
    const rest = segments.slice(own.length);
    if (!rest.length && route.loadComponent) return route;
    const child = route.children && findPage(route.children, rest);
    if (child) return child;
  }
  return undefined;
}

/** Reload the signed-in profile. A failed refresh must not block bootstrap. */
export function restoreAuthenticatedSession(auth: AuthService): Promise<unknown> {
  if (!auth.isAuthenticated()) {
    return Promise.resolve(null);
  }
  return firstValueFrom(
    auth.refreshIfExpired().pipe(
      switchMap(() => (auth.isAuthenticated() ? auth.getProfile() : of(null))),
      catchError((error) => {
        console.warn('Failed to refresh user data:', error);
        // The server refused the token: the session ended on its own (endSession already ran if the refresh
        // token was refused, and the user is signed out then)
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (auth.isAuthenticated()) auth.endSession();
        } else if (!auth.currentUser()) {
          auth.logout();
        }
        return of(null);
      })
    )
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAppInitializer(() => preloadOpenedPage(inject(AuthService))),
    provideAppInitializer(() => restoreAuthenticatedSession(inject(AuthService))),
    provideAppInitializer(() => {
      inject(AppVersionService).start();
    }),
    provideAppInitializer(() => {
      inject(LanguageSyncService).start();
    }),
    provideAppInitializer(() => {
      inject(SelectSheetService).start();
    }),
    provideRouter(
      routes,
      withNavigationErrorHandler((event) => inject(ChunkReloadService).handle(event.error))
    ),
    provideHttpClient(
      // pageProgress first: a token refresh and its retry count as the one page request
      withInterceptors([
        pageProgressInterceptor,
        authInterceptor,
        errorInterceptor,
        orderCacheInterceptor,
      ])
    ),
    provideAnimationsAsync(),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
